/**
 * Markers — annotated 3D points the viewer renders as billboarded dots that
 * expand into a title + body popover on click. Schema entry per
 * documentation/scene-schema.md:
 *
 *   markers[] = { id, position: [x, y, z], title, body }
 *
 * Editor responsibilities:
 *   - hold the marker list in memory
 *   - render each marker as a vertical bar + cross in the viewport so the
 *     user sees where they've been placed
 *   - expose add / remove / move / setTitle / setBody via events
 *   - serialize to scene.json on export + .ssproj on save
 *
 * Marker positions are world-space Vec3. The viewport renderer reuses the
 * same line-mesh + debug-shader pattern as WalkPath so this Element fits
 * the existing gizmo layer without new infrastructure.
 */

import {
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_LINES,
    SEMANTIC_COLOR,
    SEMANTIC_POSITION,
    ShaderMaterial,
    Vec3
} from 'playcanvas';
import { seedLineMesh } from './line-mesh';

import { Element, ElementType } from '../element';
import type { Events } from '../events';
import type { Scene } from '../scene';
import { vertexShader, fragmentShader } from '../shaders/debug-shader';

export interface MarkerData {
    id: string;
    position: Vec3;
    title: string;
    body: string;
}

// Vertical bar (1 line) + 4-arm cross at top (4 lines) = 5 lines per marker
const LINES_PER_MARKER = 5;
const STEM_HEIGHT = 0.4;      // world-space stem length
const CROSS_HALF = 0.08;

let _markerIdSeq = 1;
const newMarkerId = () => `m${(Date.now() % 1_000_000).toString(36)}${(_markerIdSeq++).toString(36)}`;

class Markers extends Element {
    entity: Entity;
    mesh: Mesh;
    material: ShaderMaterial;
    meshInstance: MeshInstance;
    dirty = true;

    private _markers: MarkerData[] = [];
    private _selectedId: string | null = null;
    private _visible = true;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'markersMaterial',
            attributes: {
                vertex_position: SEMANTIC_POSITION,
                vertex_color: SEMANTIC_COLOR
            },
            vertexWGSL: vertexShader,
            fragmentWGSL: fragmentShader
        });
        this.material.depthWrite = false;
        this.material.depthTest = false;
        this.material.update();

        this.mesh = new Mesh(device);
        this.mesh.primitive[0] = {
            baseVertex: 0,
            type: PRIMITIVE_LINES,
            base: 0,
            count: 0
        };
        seedLineMesh(this.mesh);
        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('markers');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.gizmoLayer.id]
        });
        scene.app.root.addChild(this.entity);

        registerMarkersEvents(this, scene.events);
    }

    destroy() {
        this.entity?.destroy();
    }

    onPreRender() {
        const visible = this._visible && this.scene.camera.renderOverlays;
        if (visible && this.dirty) {
            this.dirty = false;
            this.rebuildMesh();
        }
        // an empty line mesh is a zero-vertex draw on WebGPU: keep it off
        this.entity.enabled = visible && this.mesh.primitive[0].count > 0;
    }

    // ---- API ------------------------------------------------------------

    get markers(): readonly MarkerData[] { return this._markers; }
    get selectedId(): string | null { return this._selectedId; }
    get visible(): boolean { return this._visible; }

    // Named `addMarker` (not `add`) because Element's `add()` is the lifecycle
    // hook called by Scene.add() — overloading would shadow it. Same reason
    // for `removeMarker`/`moveMarker` below: Element.remove() takes zero args
    // (lifecycle teardown), Element.move(position?, rotation?, scale?) is the
    // transform setter. We need different signatures, so different names.
    addMarker(position: Vec3, title = '', body = ''): MarkerData {
        const m: MarkerData = {
            id: newMarkerId(),
            position: new Vec3(position.x, position.y, position.z),
            title,
            body
        };
        this._markers.push(m);
        this._selectedId = m.id;
        this.markDirty();
        this.scene.events.fire('markers.changed');
        this.scene.events.fire('markers.selectionChanged', m.id);
        return m;
    }

    removeMarker(id: string): boolean {
        const idx = this._markers.findIndex(m => m.id === id);
        if (idx === -1) return false;
        this._markers.splice(idx, 1);
        if (this._selectedId === id) {
            this._selectedId = this._markers[Math.min(idx, this._markers.length - 1)]?.id ?? null;
            this.scene.events.fire('markers.selectionChanged', this._selectedId);
        }
        this.markDirty();
        this.scene.events.fire('markers.changed');
        return true;
    }

    moveMarker(id: string, position: Vec3): boolean {
        const m = this._markers.find(x => x.id === id);
        if (!m) return false;
        m.position.set(position.x, position.y, position.z);
        this.markDirty();
        this.scene.events.fire('markers.changed');
        return true;
    }

    /**
     * Apply an in-place world transform to every marker position. Used when the
     * whole scene is re-oriented (e.g. walk-tool floor leveling rotates all
     * splats) so markers travel with the geometry instead of being left behind
     * at their old absolute positions. The callback mutates each Vec3 directly.
     */
    transformPositions(fn: (p: Vec3) => void): void {
        if (this._markers.length === 0) return;
        for (const m of this._markers) fn(m.position);
        this.markDirty();
        this.scene.events.fire('markers.changed');
    }

    setTitle(id: string, title: string): boolean {
        const m = this._markers.find(x => x.id === id);
        if (!m) return false;
        m.title = title;
        this.scene.events.fire('markers.changed');
        return true;
    }

    setBody(id: string, body: string): boolean {
        const m = this._markers.find(x => x.id === id);
        if (!m) return false;
        m.body = body;
        this.scene.events.fire('markers.changed');
        return true;
    }

    select(id: string | null) {
        if (id === this._selectedId) return;
        this._selectedId = id;
        this.markDirty();
        this.scene.events.fire('markers.selectionChanged', id);
    }

    setVisible(v: boolean) {
        if (v === this._visible) return;
        this._visible = v;
        this.scene.forceRender = true;
        this.scene.events.fire('markers.visibleChanged', v);
    }

    clear() {
        if (this._markers.length === 0) return;
        this._markers.length = 0;
        this._selectedId = null;
        this.markDirty();
        this.scene.events.fire('markers.changed');
        this.scene.events.fire('markers.selectionChanged', null);
    }

    /** Public scene.json shape. Empty array → omit. */
    serialize(): MarkerData[] | null {
        if (this._markers.length === 0) return null;
        return this._markers.map(m => ({
            id: m.id,
            position: new Vec3(m.position.x, m.position.y, m.position.z),
            title: m.title,
            body: m.body
        }));
    }

    /** Round-trip from .ssproj. Tolerant: skips malformed entries. */
    deserialize(data: any[]) {
        if (!Array.isArray(data)) return;
        this._markers = data
            .filter(d => d && Array.isArray(d.position) && d.position.length >= 3)
            .map(d => ({
                id: typeof d.id === 'string' && d.id.length > 0 ? d.id : newMarkerId(),
                position: new Vec3(d.position[0], d.position[1], d.position[2]),
                title: typeof d.title === 'string' ? d.title : '',
                body: typeof d.body === 'string' ? d.body : ''
            }));
        this._selectedId = this._markers[0]?.id ?? null;
        this.markDirty();
        this.scene.events.fire('markers.changed');
        this.scene.events.fire('markers.selectionChanged', this._selectedId);
    }

    // ---- internals ------------------------------------------------------

    private markDirty() {
        this.dirty = true;
        this.scene.forceRender = true;
    }

    private rebuildMesh() {
        const n = this._markers.length;
        if (n === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        // 5 lines per marker × 2 vertices per line.
        const lineCount = n * LINES_PER_MARKER;
        const vertCount = lineCount * 2;
        const positions = new Float32Array(vertCount * 3);
        const colors = new Uint8Array(vertCount * 4);
        let pi = 0;
        let ci = 0;

        const ORANGE: [number, number, number] = [255, 153, 0];
        const SELECTED: [number, number, number] = [255, 220, 0];     // yellow when selected

        const pushLine = (ax: number, ay: number, az: number, bx: number, by: number, bz: number, rgb: [number, number, number]) => {
            positions[pi++] = ax; positions[pi++] = ay; positions[pi++] = az;
            positions[pi++] = bx; positions[pi++] = by; positions[pi++] = bz;
            for (let k = 0; k < 2; k++) {
                colors[ci++] = rgb[0]; colors[ci++] = rgb[1]; colors[ci++] = rgb[2]; colors[ci++] = 255;
            }
        };

        for (const m of this._markers) {
            const rgb = m.id === this._selectedId ? SELECTED : ORANGE;
            const { x, y, z } = m.position;
            // Vertical stem rising from the position.
            pushLine(x, y, z,    x, y + STEM_HEIGHT, z,    rgb);
            // 4-arm cross at top so the marker is visually unambiguous.
            const top = y + STEM_HEIGHT;
            pushLine(x - CROSS_HALF, top, z,  x + CROSS_HALF, top, z,  rgb);
            pushLine(x, top, z - CROSS_HALF,  x, top, z + CROSS_HALF,  rgb);
            pushLine(x - CROSS_HALF * 0.7, top + CROSS_HALF * 0.7, z,  x + CROSS_HALF * 0.7, top - CROSS_HALF * 0.7, z, rgb);
            pushLine(x - CROSS_HALF * 0.7, top - CROSS_HALF * 0.7, z,  x + CROSS_HALF * 0.7, top + CROSS_HALF * 0.7, z, rgb);
        }

        this.mesh.setPositions(Array.from(positions));
        this.mesh.setColors32(colors);
        this.mesh.update(PRIMITIVE_LINES);
    }
}

// ----------------------------------------------------------------------------
// Event wiring — `markers.*` namespace, mirrors the walk-path pattern.
// ----------------------------------------------------------------------------

const registerMarkersEvents = (markers: Markers, events: Events) => {
    events.function('markers', () => markers);
    events.function('markers.serialize', () => markers.serialize());

    events.on('markers.add', (position: Vec3, title?: string, body?: string) => markers.addMarker(position, title ?? '', body ?? ''));
    events.on('markers.remove', (id: string) => markers.removeMarker(id));
    events.on('markers.move', (id: string, position: Vec3) => markers.moveMarker(id, position));
    events.on('markers.setTitle', (id: string, title: string) => markers.setTitle(id, title));
    events.on('markers.setBody', (id: string, body: string) => markers.setBody(id, body));
    events.on('markers.select', (id: string | null) => markers.select(id));
    events.on('markers.setVisible', (v: boolean) => markers.setVisible(v));
    events.on('markers.clear', () => markers.clear());
    events.on('markers.deserialize', (data: any[]) => markers.deserialize(data));

    events.on('scene.clear', () => markers.clear());
};

export { Markers };
